import axios from "axios";

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid; let app-level logic handle redirect to avoid hard reload loop
      localStorage.removeItem("token");

      //redirect to login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Dashboard API
export const dashboardAPI = {
  getCounts: () => api.get("/dashboard/counts"), // returns { success, message, data:{partners, orders, users, sleeves} }
};

// Auth API
export const authAPI = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  logout: () => api.post("/auth/logout"),
  getProfile: () => api.get("/auth/profile"),
  refreshToken: () => api.post("/auth/refresh-token"),
};

export const meAPI = {
  getPermissions: () => api.get("/me/permissions"),
};

// Users API
export const usersAPI = {
  getUsers: (params) => api.get("/users", { params }),
  getUserById: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post("/users", userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  importUsers: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/users/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updateUsersFromExcel: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/users/import/update", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getRoles: (params) => api.get("/roles", { params }),
  getAllBDMs: () => api.get("/users/all"),
};

// Partners API (placeholder for future implementation)
export const partnersAPI = {
  getPartners: (params) => api.get("/partners", { params }),
  createPartner: (partnerData) =>
    api.post("/partners", partnerData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  updatePartner: (id, partnerData) =>
    api.put(`/partners/${id}`, partnerData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deletePartner: (id) => api.delete(`/partners/${id}`),
  importPartners: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/partners/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Orders API (placeholder for future implementation)
export const ordersAPI = {
  getOrders: (params) => api.get("/orders", { params }),
  createOrder: (orderData) => api.post("/orders", orderData),
  updateOrder: (id, orderData) => api.put(`/orders/${id}`, orderData),
  deleteOrder: (id) => api.delete(`/orders/${id}`),

  importOrders: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/orders/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r);
  },
  uploadClientDetails: (orderId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("order_id", orderId);

    return api.post(`/orders/clients/import/${orderId}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updatePaymentStatusWithProof: (orderId, { status, file, mode, remark }) => {
    const fd = new FormData();
    fd.append("payment_status", status); // "Received"
    fd.append("payment_mode", mode); // e.g., "UPI", "Cash", etc.
    fd.append("sales_payment_remarks", remark);
    fd.append("file", file); // the image/proof

    return api.post(`/orders/update-payment-status/${orderId}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  updatePaymentConfirmed: (orderId, { status, remark }) => {
    return api.post(`/orders/confirm-payment/${orderId}`, {
      payment_confirmed: true,
      account_payment_remarks: remark,
    });
  },

  updateSampleDesignReady: (orderId, isReady) =>
    api.patch(`/orders/update-sample-design-ready-status/${orderId}`, {
      status: !!isReady,
    }),

  updateApprovedByPartner: (orderId, isApproved) =>
    api.patch(`/orders/update-approval-by-partner-status/${orderId}`, {
      status: !!isApproved,
    }),

  updatePrintStatus: (orderId, { status, remarks, printFileDate }) =>
    api.patch(`/orders/update-print-status/${orderId}`, {
      status, // "Pending" | "Done"
      print_remarks: remarks ?? null, // required when Done
      print_file_date: printFileDate ?? null, // ISO string (set when Done)
    }),

  downloadPrintFile: (orderId) =>
    api.post(
      `/orders/generate-print-file/${orderId}`,
      {},
      {
        responseType: "arraybuffer", // get raw bytes
        transformResponse: [(data) => data], // don't auto-parse
        headers: { Accept: "application/pdf" },
      }
    ),

  updateSleeveHandoverToWarehouse: (orderId, isSent) =>
    api.patch(`/orders/update-sleeve-handover-status/${orderId}`, {
      status: !!isSent,
    }),

  updateSleeveReceivedToWarehouse: (orderId, isReceived) =>
    api.patch(`/orders/update-sleeve-received-status/${orderId}`, {
      status: !!isReceived,
    }),

  importDispatch: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post("/orders/import-dispatch", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r); // keep same convention as your other imports
  },

  importDeliveryData: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/orders/import-delivered`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r); // backend returns { isSuccess, data, message }
  },

  importRedispatchData: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/orders/import-redispatch`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r); // backend returns { isSuccess, data, message }
  },

  getDispatches: (orderId, params = {}) =>
    api.get(`/orders/${orderId}/dispatches`, { params }),

  getDeliveries: (orderId, params = {}) =>
    api.get(`/orders/${orderId}/deliveries`, { params }),

  getRedispatches: (orderId, params = {}) =>
    api.get(`/orders/${orderId}/re-dispatches`, { params }),

  exportOrders: (params = {}) =>
    api.post(`/orders/exports`, params, { responseType: "blob" }),
};

// Sleeves API (placeholder for future implementation)
// export const sleevesAPI = {
//   getSleeves: (params) => api.get("/sleeves", { params }),
//   createSleeve: (sleeveData) => api.post("/sleeves", sleeveData),
//   updateSleeve: (id, sleeveData) => api.put(`/sleeves/${id}`, sleeveData),
//   deleteSleeve: (id) => api.delete(`/sleeves/${id}`),
//   importSleeves: (file) => {
//     const formData = new FormData();
//     formData.append("file", file);
//     return api.post("/sleeves/import", formData, {
//       headers: { "Content-Type": "multipart/form-data" },
//     });
//   },
// };

export const sleevesAPI = {
  getSleeves: (params) => api.get("/sleeves", { params }),
  createSleeve: (sleeveData) => api.post("/sleeves", sleeveData),
  updateSleeve: (id, sleeveData) => api.put(`/sleeves/${id}`, sleeveData),
  deleteSleeve: (id) => api.delete(`/sleeves/${id}`),
  importSleeves: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/sleeves/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

export const couriersAPI = {
  getCouriers: (params) => api.get("/couriers", { params }),
  createCourier: (courierData) => api.post("/couriers", courierData),
  updateCourier: (id, courierData) => api.put(`/couriers/${id}`, courierData),
  deleteCourier: (id) => api.delete(`/couriers/${id}`),
  importCouriers: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/couriers/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Occasion API
export const occasionAPI = {
  getOccasionMonths: () => api.get("/orders/occasion-months"),
};

export default api;
